import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Search } from 'lucide-react';
import { api, type X5Application } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { SortableHeader } from '../components/ui/SortableHeader';
import { Drawer } from '../components/ui/Drawer';
import { Fact, FactSection } from '../components/ui/Facts';
import { ServerCell, ServerFacts } from '../components/ServerFacts';
import { useSort } from '../lib/sort';
import { formatDateTime } from '../lib/format';
import { useOrgLookup } from '../lib/orgLookup';

type EnrichedApp = X5Application & { organizationName: string };

const getAppValue = (item: EnrichedApp, key: string): unknown => {
  switch (key) {
    case 'displayName':
      return item.displayName ?? '';
    case 'application':
      return item.application ?? '';
    case 'stack':
      return item.stack ?? '';
    case 'fleet':
      return item.fleet ?? '';
    case 'server': {
      if (item.server?.name) return item.server.name.toLowerCase();
      // Unresolved rows sort last rather than interleaving by URL text.
      return `￿${item.x5serverIP ?? ''}`;
    }
    case 'organization':
      return item.organizationName;
    default:
      return undefined;
  }
};

export function Applications() {
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<EnrichedApp | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.listApplications(),
  });

  const lookup = useOrgLookup();

  const enriched = useMemo<EnrichedApp[]>(
    () =>
      (data?.items ?? []).map((a) => ({
        ...a,
        organizationName: lookup.resolveName(a.organizationId),
      })),
    [data, lookup],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return enriched;
    const needle = q.toLowerCase();
    return enriched.filter(
      (a) =>
        a.displayName?.toLowerCase().includes(needle) ||
        a.application?.toLowerCase().includes(needle) ||
        a.stack?.toLowerCase().includes(needle) ||
        a.fleet?.toLowerCase().includes(needle) ||
        a.x5serverIP?.toLowerCase().includes(needle) ||
        a.server?.name?.toLowerCase().includes(needle) ||
        a.server?.instanceId?.toLowerCase().includes(needle) ||
        a.organizationName.toLowerCase().includes(needle) ||
        a.organizationId?.toLowerCase().includes(needle) ||
        a.id.toLowerCase().includes(needle),
    );
  }, [enriched, q]);

  const { sortedItems, sort, toggle } = useSort(filtered, getAppValue);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Applications</h1>
          <p className="text-muted-foreground">
            {data ? `${filtered.length} of ${data.items.length} applications` : '—'}
          </p>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, stack, server, organization…"
            className="pl-9"
          />
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">{(error as Error).message}</p>}

      {data?.serversPartial && (
        <p className="flex items-start gap-2 text-sm text-caution">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          Part of the server estate could not be read, so an application shown without an instance
          may be a gap rather than a stale row.
        </p>
      )}

      {data && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <SortableHeader
                    label="Display name"
                    sortKey="displayName"
                    sort={sort}
                    onToggle={toggle}
                  />
                  <SortableHeader
                    label="Application"
                    sortKey="application"
                    sort={sort}
                    onToggle={toggle}
                  />
                  <SortableHeader label="Stack" sortKey="stack" sort={sort} onToggle={toggle} />
                  <SortableHeader label="Fleet" sortKey="fleet" sort={sort} onToggle={toggle} />
                  <SortableHeader label="Server" sortKey="server" sort={sort} onToggle={toggle} />
                  <SortableHeader
                    label="Organization"
                    sortKey="organization"
                    sort={sort}
                    onToggle={toggle}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium">{a.displayName ?? '—'}</td>
                    <td className="px-4 py-3 font-mono text-xs">{a.application ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.stack ?? '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.fleet ?? '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <ServerCell
                        server={a.server ?? null}
                        address={a.x5serverIP ?? '—'}
                        onOpen={() => setDetail(a)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      {a.organizationId ? (
                        <Link
                          to={`/organizations/${a.organizationId}`}
                          className="text-primary hover:underline"
                        >
                          {a.organizationName}
                        </Link>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
                {sortedItems.length === 0 && (
                  <tr>
                    <td className="px-4 py-6 text-center text-muted-foreground" colSpan={6}>
                      No matches.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ApplicationDrawer application={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

/**
 * An X5 Application row plus the EC2 instance behind its `x5serverIP`. Same
 * panel shape as the SMS Numbers drawer, and the same reason: the row carries
 * more than the table can show, and the server it points at is the part an
 * operator actually acts on.
 */
function ApplicationDrawer({
  application,
  onClose,
}: {
  application: EnrichedApp | null;
  onClose: () => void;
}) {
  return (
    <Drawer
      open={application !== null}
      onClose={onClose}
      title={
        application && (
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">
              {application.displayName ?? application.application ?? application.id}
            </div>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              {application.organizationName}
            </p>
          </div>
        )
      }
    >
      {application && (
        <div className="space-y-6">
          <FactSection title={application.server?.name ?? 'Server'}>
            <Fact label="Server URL" value={application.x5serverIP} mono />
            {application.server ? (
              <ServerFacts server={application.server} />
            ) : (
              <Fact label="Instance" value="No EC2 instance holds this address" />
            )}
          </FactSection>

          <FactSection title="Application">
            <Fact label="Display name" value={application.displayName} />
            <Fact label="Application" value={application.application} mono />
            <Fact label="Stack" value={application.stack} />
            <Fact label="Fleet" value={application.fleet} />
            <Fact
              label="Organization"
              value={
                application.organizationId ? (
                  <Link
                    to={`/organizations/${encodeURIComponent(application.organizationId)}`}
                    className="text-accent hover:underline"
                  >
                    {application.organizationName}
                  </Link>
                ) : null
              }
            />
            <Fact label="Created" value={formatDateTime(application.createdAt)} />
            <Fact label="Updated" value={formatDateTime(application.updatedAt)} />
            <Fact label="Record id" value={application.id} mono />
          </FactSection>
        </div>
      )}
    </Drawer>
  );
}
